/**
 * Renders a ticket description or note body.
 *
 * **Renders the markdown *source*, not the server's `_html` field.** That is deliberate. The
 * backend sanitises (§12) and stores both, and the API returns both — but piping the server HTML
 * into the DOM would need `dangerouslySetInnerHTML`, which this frontend does not use anywhere
 * (D-014). Instead `renderMarkdown` builds React elements from the source, so raw HTML in a
 * user's text becomes visible text rather than markup.
 *
 * The result is two independent defences rather than one: a bleach bypass on the server still
 * cannot produce an executing element here, because nothing on this path can produce an element
 * the renderer didn't construct itself.
 *
 * The `_html` fields stay in the API for consumers that genuinely need HTML — outbound email and
 * the client portal, in later phases.
 */

import { renderMarkdown } from '@/utils/markdown';

export function MarkdownBody({ source }: { source: string | null | undefined }) {
  if (!source) return null;
  const { blocks } = renderMarkdown(source);
  return (
    <div className="space-y-2 text-sm text-surface-700 dark:text-surface-200">{blocks}</div>
  );
}
